// Route: /flashcards/marked — все карточки с marked=true (виртуальный набор).
// URL state: ?demo=loading|empty|error
//
// См. docs/ui-spec/flashcards.marked.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { TactileToken } from "@studyhub/ui/components/studyhub/tactile-token";
import { toast } from "@studyhub/ui/toast";
import {
	IconArrowLeft,
	IconBookmark,
	IconBookmarkFilled,
	IconCards,
	IconClockHour4,
	IconLayersIntersect,
	IconSparkles,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	type FlashcardDto,
	useDeleteFlashcard,
	useFlashcardSets,
	useMarkedFlashcards,
	useToggleFlashcardMark,
} from "@/components/flashcards/_api";
import { CardEditorDialog } from "@/components/flashcards/set-detail/card-editor-dialog";
import { CardsTable } from "@/components/flashcards/set-detail/cards-table";
import { CountUp } from "@/components/shared/count-up";
import { LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";

const searchSchema = z.object({
	demo: z.enum(["default", "loading", "empty", "error"]).optional(),
	q: z.string().optional(),
});

export const Route = createFileRoute("/flashcards/marked")({
	validateSearch: tolerantSearch(searchSchema),
	component: MarkedPage,
	pendingComponent: PagePendingFallback,
});

function MarkedPage(): React.ReactElement {
	const { t } = useTranslation("flashcards");
	const search = Route.useSearch();
	const [searchQ, setSearchQ] = useState(search.q ?? "");
	const [editorOpen, setEditorOpen] = useState(false);
	const [editorCard, setEditorCard] = useState<FlashcardDto | undefined>();

	const markedQuery = useMarkedFlashcards();
	const setsQuery = useFlashcardSets();
	const deleteCard = useDeleteFlashcard();
	const toggleMark = useToggleFlashcardMark();

	const isLoading = search.demo === "loading" || markedQuery.isLoading;
	const isError = search.demo === "error" || markedQuery.isError;

	const setMap = useMemo(() => {
		const m = new Map<string, { name: string; color: string }>();
		for (const s of setsQuery.data?.items ?? []) m.set(s.id, { name: s.name, color: s.color });
		return m;
	}, [setsQuery.data]);

	const allCards = useMemo(() => markedQuery.data?.items ?? [], [markedQuery.data]);
	const filtered = useMemo(() => {
		let items = allCards;
		if (search.demo === "empty") items = [];
		if (searchQ) {
			const q = searchQ.toLowerCase();
			items = items.filter(
				(c) => c.front.toLowerCase().includes(q) || c.back.toLowerCase().includes(q),
			);
		}
		return items;
	}, [allCards, searchQ, search.demo]);

	const total = allCards.length;
	const due = allCards.filter((c) => new Date(c.srsDueAt).getTime() <= Date.now()).length;
	const newCount = allCards.filter((c) => c.srsState === "new").length;
	const setsCount = new Set(allCards.map((c) => c.setId)).size;

	return (
		<div className={LIST_PAGE_SHELL}>
			{/* Header */}
			<SectionHeader
				icon={<IconBookmarkFilled />}
				category="streak"
				title={t("marked.title")}
				// description переносится в 2 строки (line-clamp-2): subtitle + хинт об
				// авто-снятии больше не режутся в один truncate-огрызок на mobile.
				description={`${t("marked.subtitle", { total, due })} · ${t("marked.autoUnmarkHint")}`}
				stackActions
				actions={
					total > 0 ? (
						<Button asChild className="max-sm:w-full">
							<Link to="/flashcards/study/$setId" params={{ setId: "marked" }}>
								<IconCards size={16} />
								{t("marked.studyAllCta", { n: due })}
							</Link>
						</Button>
					) : undefined
				}
			/>

			{/* KPI — тактильные токены */}
			{!isLoading && total > 0 && (
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
					<TactileToken
						category="streak"
						interactive={false}
						icon={<IconClockHour4 />}
						value={<CountUp value={due} />}
						label={t("library.kpi.due")}
					/>
					<TactileToken
						category="cards"
						interactive={false}
						icon={<IconSparkles />}
						value={<CountUp value={newCount} />}
						label={t("library.kpi.new")}
					/>
					<TactileToken
						category="time"
						interactive={false}
						icon={<IconBookmark />}
						value={<CountUp value={total} />}
						label={t("marked.kpi.total")}
					/>
					<TactileToken
						category="mastery"
						interactive={false}
						icon={<IconLayersIntersect />}
						value={<CountUp value={setsCount} />}
						label={t("marked.kpi.sets")}
					/>
				</div>
			)}

			{/* Toolbar — поиск нужен только при наличии карточек. Унифицирован
			    через ListToolbar (как set-detail), без grid/list-переключателя. */}
			{!isLoading && !isError && total > 0 && (
				<ListToolbar
					search={{
						value: searchQ,
						onChange: setSearchQ,
						placeholder: t("setDetail.toolbar.search"),
					}}
					showViewToggle={false}
				/>
			)}

			{/* Body */}
			{isLoading ? (
				// Скелетон имитирует mobile card-list (а не один h-96 блок) → нет
				// layout-shift при появлении карточного layout таблицы.
				<div className="flex flex-col gap-2.5">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-24 w-full rounded-xl sm:h-14" />
					))}
				</div>
			) : isError ? (
				<ErrorState
					title={t("library.errorTitle")}
					description={t("library.errorSubtitle")}
					action={
						<Button variant="ghost" onClick={() => void markedQuery.refetch()}>
							{t("library.retry")}
						</Button>
					}
				/>
			) : filtered.length === 0 ? (
				<EmptyState
					icon={<IconBookmark size={56} stroke={1.5} className="text-muted-foreground" />}
					title={t("marked.empty.title")}
					description={t("marked.empty.subtitle")}
					action={
						<Button asChild variant="ghost">
							<Link to="/flashcards">
								<IconArrowLeft size={14} />
								{t("marked.empty.back")}
							</Link>
						</Button>
					}
				/>
			) : (
				<CardsTable
					cards={filtered}
					showSet
					setNameById={setMap}
					onEdit={(card) => {
						setEditorCard(card);
						setEditorOpen(true);
					}}
					onDelete={(id) =>
						deleteCard.mutate(
							{ id, setId: filtered.find((c) => c.id === id)?.setId },
							{ onError: () => toast.error(t("library.errorTitle")) },
						)
					}
					onToggleMark={(id) => {
						const c = filtered.find((x) => x.id === id);
						if (!c) return;
						// В marked-view размечают только unmark (убрать из набора).
						toggleMark.mutate(
							{ id, marked: !c.marked, setId: c.setId },
							{ onError: () => toast.error(t("library.errorTitle")) },
						);
					}}
				/>
			)}

			<CardEditorDialog open={editorOpen} onOpenChange={setEditorOpen} card={editorCard} />
		</div>
	);
}
